/**
 * ========================================
 * ARTICLE PAGE INTERACTIVITY
 * - Reading progress bar
 * - Sticky TOC with scroll spy
 * - Copy link functionality
 * - Smooth scroll to anchors
 * ========================================
 */

(function() {
  'use strict';

  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    initProgressBar();
    initCopyLink();
    initSmoothScroll();
    injectHeadingIds();
    buildTocFromDom();
    initScrollSpy();
  }

  /**
   * Reading Progress Bar
   * Updates width based on scroll position through article content
   */
  function initProgressBar() {
    const progressBar = document.getElementById('article-progress-bar');
    const articleContent = document.getElementById('article-content');

    if (!progressBar || !articleContent) return;

    function updateProgress() {
      const articleRect = articleContent.getBoundingClientRect();
      const articleTop = articleRect.top + window.scrollY;
      const articleHeight = articleContent.offsetHeight;
      const windowHeight = window.innerHeight;
      const scrollPosition = window.scrollY;

      // Calculate progress through the article
      const startReading = articleTop;
      const endReading = articleTop + articleHeight - windowHeight;

      let progress = 0;

      if (scrollPosition >= startReading) {
        progress = (scrollPosition - startReading) / (endReading - startReading);
        progress = Math.min(Math.max(progress, 0), 1);
      }

      progressBar.style.width = (progress * 100) + '%';
    }

    // Throttled scroll handler
    let ticking = false;
    window.addEventListener('scroll', function() {
      if (!ticking) {
        window.requestAnimationFrame(function() {
          updateProgress();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // Initial update
    updateProgress();
  }

  /**
   * Scroll Spy for Table of Contents
   * Highlights the current section in the TOC as user scrolls
   */
  function initScrollSpy() {
    const tocLinks = document.querySelectorAll('[data-toc-link]');
    const articleProse = document.getElementById('article-prose');

    if (!tocLinks.length || !articleProse) return;

    // Get all headings that have IDs (from the article content)
    const headings = articleProse.querySelectorAll('h2[id]');

    if (!headings.length) return;

    function updateActiveLink() {
      const scrollPosition = window.scrollY;
      const offset = 120; // Account for sticky header and some padding

      let currentSection = null;

      // Find the current section
      headings.forEach(function(heading) {
        const headingTop = heading.getBoundingClientRect().top + window.scrollY;

        if (scrollPosition >= headingTop - offset) {
          currentSection = heading.getAttribute('id');
        }
      });

      // Update active state on TOC links
      tocLinks.forEach(function(link) {
        const href = link.getAttribute('href');
        const targetId = href ? href.replace('#', '') : '';

        if (targetId === currentSection) {
          link.classList.add('is-active');
          link.parentElement.classList.add('is-active');
        } else {
          link.classList.remove('is-active');
          link.parentElement.classList.remove('is-active');
        }
      });
    }

    // Throttled scroll handler
    let ticking = false;
    window.addEventListener('scroll', function() {
      if (!ticking) {
        window.requestAnimationFrame(function() {
          updateActiveLink();
          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    // Initial update
    updateActiveLink();
  }

  /**
   * Inject IDs into H2 headings in article content
   * This ensures headings have IDs for TOC linking
   */
  function injectHeadingIds() {
    const articleProse = document.getElementById('article-prose');
    if (!articleProse) return;

    const headings = articleProse.querySelectorAll('h2:not([id])');

    headings.forEach(function(heading) {
      const text = heading.textContent || heading.innerText;
      const id = slugify(text);
      heading.setAttribute('id', id);
    });
  }

  /**
   * Build Table of Contents dynamically from DOM H2s
   * Bypasses Liquid's broken article.content parsing
   */
  function buildTocFromDom() {
    const tocList = document.getElementById('article-toc-list');
    if (!tocList) return;
    
    const articleProse = document.getElementById('article-prose');
    if (!articleProse) return;
    
    const headings = articleProse.querySelectorAll('h2[id]');
    if (!headings.length) return;
    
    // Clear placeholder
    tocList.innerHTML = '';
    
    headings.forEach(function(heading) {
      const id = heading.getAttribute('id');
      const text = (heading.textContent || heading.innerText).trim();
      if (!text || !id) return;
      
      const li = document.createElement('li');
      li.className = 'article-toc__item';
      
      const a = document.createElement('a');
      a.href = '#' + id;
      a.className = 'article-toc__link';
      a.setAttribute('data-toc-link', '');
      a.textContent = text;
      
      li.appendChild(a);
      tocList.appendChild(li);
    });
  }

  /**
   * Convert text to URL-friendly slug
   */
  function slugify(text) {
    return text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, '-')        // Replace spaces with -
      .replace(/[^\w\-]+/g, '')    // Remove non-word chars
      .replace(/\-\-+/g, '-')      // Replace multiple - with single -
      .replace(/^-+/, '')          // Trim - from start
      .replace(/-+$/, '');         // Trim - from end
  }

  /**
   * Copy Link Functionality
   * Copies the article URL to clipboard and shows feedback
   */
  function initCopyLink() {
    const copyButtons = document.querySelectorAll('[data-copy-url]');

    copyButtons.forEach(function(button) {
      button.addEventListener('click', function(e) {
        e.preventDefault();

        const url = button.getAttribute('data-copy-url') || window.location.href;

        // Try modern clipboard API first
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(function() {
            showCopyFeedback(button, true);
          }).catch(function() {
            fallbackCopy(url, button);
          });
        } else {
          fallbackCopy(url, button);
        }
      });
    });
  }

  /**
   * Fallback copy method for older browsers
   */
  function fallbackCopy(text, button) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-9999px';
    textArea.style.top = '-9999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      showCopyFeedback(button, true);
    } catch (err) {
      showCopyFeedback(button, false);
    }

    document.body.removeChild(textArea);
  }

  /**
   * Show visual feedback after copy attempt
   */
  function showCopyFeedback(button, success) {
    const originalLabel = button.getAttribute('aria-label');
    const originalHTML = button.innerHTML;

    if (success) {
      button.setAttribute('aria-label', 'Copied!');
      button.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"></polyline></svg>';
      button.classList.add('is-copied');
    } else {
      button.setAttribute('aria-label', 'Failed to copy');
    }

    // Reset after delay
    setTimeout(function() {
      button.setAttribute('aria-label', originalLabel);
      button.innerHTML = originalHTML;
      button.classList.remove('is-copied');
    }, 2000);
  }

  /**
   * Smooth Scroll to Anchors
   * Adds smooth scrolling behavior for TOC links
   */
  function initSmoothScroll() {
    const tocLinks = document.querySelectorAll('[data-toc-link]');

    tocLinks.forEach(function(link) {
      link.addEventListener('click', function(e) {
        const href = link.getAttribute('href');
        if (!href || !href.startsWith('#')) return;

        const targetId = href.substring(1);
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          e.preventDefault();

          const headerOffset = 100; // Account for sticky header
          const elementPosition = targetElement.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.scrollY - headerOffset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });

          // Update URL without jumping
          history.pushState(null, null, href);
        }
      });
    });
  }

})();

